import {
  AWS_BOUNCE_RATE_REVIEW,
  AWS_COMPLAINT_RATE_REVIEW,
  RATE_SIGNIFICANCE_MINIMUM,
  type CampaignResults,
  type CampaignResultsReading,
  type Channel,
} from '@wend/shared';
import { Callout, Kicker, cn } from '@wend/ui';

import { formatCount, pluralise } from '../../lib/format';
import { CampaignNotDelivered } from './CampaignNotDelivered';

/**
 * The two SES-shaped failure sections of a campaign's results, and the counter
 * they are built from.
 *
 * ## WHY THIS IS ITS OWN FILE — SLICE 24, ITEM 17
 *
 * `CampaignResultsPanel` imports the API client, which reaches `lib/config.ts`
 * and `import.meta.env`, so no verify suite can render it. Everything these
 * sections do was therefore provable only as *source text* — and the thing most
 * worth proving about them is a gate: under the "nothing has been reported"
 * sentence they must print **no counter at zero and no "the counts above are
 * exact"**, because a zero there is the absence of a measurement rather than one.
 * Slice 19 built that gate and slice 19's own review recorded that switching it
 * off left every suite green.
 *
 * Pure copy and `@wend/ui` primitives: no client, no context, no effects. A
 * suite renders it in both states and reads the words out of the markup, which
 * is the same extraction `AttachmentDeliveryNotice` is, for the same wall.
 *
 * The panel still owns the decision of *when* to render them; this file owns
 * what they say.
 */
/**
 * A rate as a percentage, to one decimal by default.
 *
 * It lived in `CampaignResultsPanel` and moved with the sections that use it —
 * `overview-format.ts` has a `formatRate` of its own with no `decimals` and its
 * own rounding rule, and importing that one here would silently change what
 * these sentences say about a bounce rate. Two functions, two call sites, both
 * deliberate.
 */
export function formatRate(rate: number, decimals = 1): string {
  return `${(rate * 100).toFixed(decimals)}%`;
}

export function SesFailureSections({
  results,
  reading,
  channel,
  unreported,
}: {
  results: CampaignResults;
  reading: CampaignResultsReading;
  channel: Channel;
  /** Every accepted message still awaits its event — `UnreportedCampaign` is above. */
  unreported: boolean;
}) {
  if (unreported) {
    /*
     * The sentence above says nothing here can tell whether a message bounced
     * or was marked as spam. So no counter at zero follows it, and no "the
     * counts above are exact": only what was reported — a complaint, and any
     * reason a message did not arrive, a delay or a rejection among them.
     */
    const anyReason = Object.values(results.notDelivered).some((count) => count > 0);
    return (
      <>
        {results.complaints > 0 && (
          <div className="flex flex-col gap-3 pt-2">
            <Kicker rule>
              Complaints
            </Kicker>
            <dl className="flex flex-wrap gap-2.5">
              <Counter label="Complaints" value={results.complaints} tone="text-bad" />
            </dl>
          </div>
        )}
        {anyReason && <CampaignNotDelivered breakdown={results.notDelivered} channel={channel} />}
      </>
    );
  }

  return (
    <>
      {/* Bounces and complaints */}
      <div className="flex flex-col gap-3 pt-2">
        <Kicker rule>
          Bounces and complaints
        </Kicker>

        <dl className="flex flex-wrap gap-2.5">
          <Counter
            label="Hard bounces"
            value={results.bounces.hard}
            tone={results.bounces.hard > 0 ? 'text-bad' : undefined}
          />
          <Counter label="Soft bounces" value={results.bounces.soft} />
          {results.bounces.undetermined > 0 && (
            <Counter label="Undetermined" value={results.bounces.undetermined} />
          )}
          <Counter
            label="Complaints"
            value={results.complaints}
            tone={results.complaints > 0 ? 'text-bad' : undefined}
          />
        </dl>

        {/*
         * THREE DEFINITIONS, NOT A PARAGRAPH. Hard against soft is the
         * distinction this section exists to keep — one address is finished and
         * the other is fine — and it was previously the middle of six lines of
         * prose, where the two sentences that had to be contrasted were not
         * beside each other. A `<dl>` puts each definition against the counter
         * it defines. Nothing was dropped: what each one meant *for the contact*
         * is the second half of every line, because that is the half somebody
         * edits a contact list on.
         */}
        <dl className="flex max-w-prose flex-col gap-1.5 text-[0.85rem] leading-relaxed text-muted">
          <div>
            <dt className="inline font-medium text-text">Hard bounce — </dt>
            <dd className="inline">
              the address does not exist or refused permanently. Suppressed; no future campaign will
              try it.
            </dd>
          </div>
          <div>
            <dt className="inline font-medium text-text">Soft bounce — </dt>
            <dd className="inline">
              the mailbox did not accept it this time and may tomorrow. Nobody is suppressed.
            </dd>
          </div>
          <div>
            <dt className="inline font-medium text-text">Complaint — </dt>
            <dd className="inline">
              delivered, then marked as spam. Suppressed, but the delivery happened, so its credit
              stays consumed.
            </dd>
          </div>
        </dl>

        {/* The threshold stays in the sentence with the rate. "4%" is a number;
            "4%, and Amazon reviews an account above 5%" is a decision. */}
        {reading.bounceRateConcerning && (
          <Callout tone="warn" title="This campaign's bounce rate is worth reviewing">
            {formatRate(reading.bounceRate)} of accepted messages have bounced — at or above the{' '}
            {formatRate(AWS_BOUNCE_RATE_REVIEW)} Amazon reviews an account against. This is one
            campaign, not the account-wide figure AWS watches.
          </Callout>
        )}
        {reading.complaintRateConcerning && (
          <Callout tone="warn" title="This campaign's complaint rate is worth reviewing">
            {formatRate(reading.complaintRate, 2)} of accepted messages were reported as spam — at or
            above the {formatRate(AWS_COMPLAINT_RATE_REVIEW, 2)} Amazon reviews an account against.
            One campaign, not the account AWS watches.
          </Callout>
        )}
        {!reading.ratesAreSignificant && (
          <p className="font-mono text-[0.68rem] uppercase tracking-[0.1em] text-muted">
            {/* SLICE 28 left this sentence in place deliberately. It looked like
                a sibling of the volume-floor justifications cut elsewhere, but it
                is not: it is attached to a COUNT the reader can see, and without
                it the absence of a percentage beside a visible number reads as a
                missing figure rather than as a deliberate refusal. The cuts in
                this slice removed explanations of absent THINGS; this explains a
                present one. */}
            Not enough volume to flag a rate — under{' '}
            {formatCount(RATE_SIGNIFICANCE_MINIMUM)} accepted messages one bounce moves the
            percentage too far. The counts above are exact.
          </p>
        )}
      </div>

      {/* Why the ones that did not arrive did not arrive — five outcomes, never
          summed. The counters above split the bounced messages three ways; these
          five answer a wider question, and two of them never became a bounce. */}
      <CampaignNotDelivered breakdown={results.notDelivered} channel={channel} />
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Counter — one count in a tile, shared by every campaign panel               */
/* -------------------------------------------------------------------------- */

/**
 * One labelled count, drawn as a small tile: bounces and complaints here, the
 * credits block on the results panel, the recipient counters on Progress, and
 * the WhatsApp failure count.
 *
 * This comment used to describe the credits block — it sat above `Counter`
 * after the credits markup moved back into `CampaignResultsPanel`, and it
 * explained a ledger rule this function knows nothing about. The ledger rule is
 * stated where the credits are drawn.
 *
 * SLICE 66: THE COUNT IS SANS, NOT MONO, AND THE LABEL IS THE SHARED KICKER.
 * These were a 1.35rem mono numeral under a hand-rolled label. Mono's dotted
 * zero reads as an 8 at this size, and a results screen is exactly where "0
 * failed" and "8 failed" must never be confused — so the figure is sans with
 * tabular digits, which still line up, and the label is the Blueprint's one
 * kicker role. The tile (a hairline over a 4% white wash) is the Blueprint's
 * metric cell; it gives every count the same box, so a row of four reads as
 * four measurements rather than a sentence of numbers.
 *
 * The tone colours only the number, and only for a verdict: a non-zero failure
 * is red because it is bad news, never as decoration. A caller that passes no
 * tone gets `text-muted`, so a plain zero does not shout. Progress and the
 * WhatsApp delivery section import this rather than keeping their own copies,
 * which both did until slice 66.
 */
export function Counter({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className="flex min-w-[6.5rem] flex-col gap-1 rounded-base border border-line bg-white/[0.04] px-3.5 py-2.5">
      <Kicker as="dt">{label}</Kicker>
      <dd
        className={cn(
          'text-[1.35rem] font-semibold leading-tight tracking-[-0.02em] tabular-nums',
          tone ?? 'text-muted',
        )}
      >
        {formatCount(value)}
      </dd>
    </div>
  );
}
